/*
 * pdp-buy-bar.js — reveals the desktop purchase bar once the buy box is gone.
 *
 * This is the ONLY thing in that bar which needs a script, and it is worth
 * being precise about why. `snippets/sticky-buy-bar.liquid` holds no state:
 * its tier switcher is a set of `<label for>`s pointing at the buy box's own
 * radios, and its buttons are form-associated submits revealed by CSS. Both
 * work with JavaScript disabled. What CSS cannot express is "appear once
 * another element has scrolled out of view", so that — and nothing else —
 * lives here.
 *
 * WHICH WAY IT FAILS. The bar is hidden by default in the snippet's own
 * stylesheet and this file only ever REVEALS it. If the script is blocked,
 * errors, or never loads, the bar stays away and the page sells through the
 * buy box exactly as it does today. Written the other way round a single error
 * would strand a bar over the footer of every product page. The money path
 * already sells without script; nothing here may put that at risk.
 *
 * IT IS A SEPARATE FILE FROM `pdp-stories.js` deliberately. That one owns
 * video playback and binds to clicks and `hashchange`; this one owns a
 * scroll-position observer. They share no state and have no reason to fail
 * together, and a page can want one without the other.
 *
 * WHY TWO OBSERVERS RATHER THAN A SCROLL HANDLER. A scroll listener runs on
 * every frame of every scroll and has to measure the page itself, which is
 * both the expensive way and the one that goes wrong when the layout changes
 * underneath it — and this page has a sticky gallery column and an accordion
 * that both change height. IntersectionObserver is told once what to watch and
 * reports only when the answer changes.
 */

const bar = document.querySelector('[data-buy-bar]')

if (bar && typeof IntersectionObserver === 'function') {
  /*
   * The form is the buy box itself, named by the same `form_id` its buttons
   * carry — so the thing being watched is exactly the thing this bar stands in
   * for. If it is missing something is very wrong upstream and the bar stays
   * hidden rather than guessing.
   */
  const buyBox = document.getElementById(bar.getAttribute('data-buy-bar'))

  /*
   * Shopify wraps the footer sections in a group whose class is stable across
   * section ids, which the id is not.
   */
  const footer = document.querySelector('.shopify-section-group-footer-group')

  if (buyBox) {
    let buyBoxOnScreen = true
    let footerOnScreen = false

    /*
     * Two independent facts, one conclusion, evaluated in one place. Toggling
     * the attribute from inside either observer instead would let the second
     * one undo the first depending on which fired last.
     */
    function sync() {
      if (buyBoxOnScreen || footerOnScreen) bar.removeAttribute('data-visible')
      else bar.setAttribute('data-visible', '')
    }

    new IntersectionObserver(
      (entries) => {
        for (const entry of entries) buyBoxOnScreen = entry.isIntersecting
        sync()
      },
      /*
       * No margin: the bar arrives as the last of the buy box leaves. A reader
       * who can still see the real button does not need a copy of it.
       */
      { threshold: 0 },
    ).observe(buyBox)

    if (footer) {
      new IntersectionObserver(
        (entries) => {
          for (const entry of entries) footerOnScreen = entry.isIntersecting
          sync()
        },
        /*
         * Also no margin, and for a symmetrical reason: the moment the footer's
         * top edge crosses the bottom of the viewport is the moment the bar
         * would start covering it.
         */
        { threshold: 0 },
      ).observe(footer)
    }

    sync()
  }
}
